import { useEffect, useMemo } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { clsx } from "clsx";
import { useAuth } from "@/state/auth";
import { usePermissions } from "@/hooks/usePermissions";
import { useUi } from "@/state/ui";
import { useToast } from "@/state/toast";
import { toast } from "@/state/toast";
import { navGroups, crumbForPath } from "@/state/nav";
import { Icon } from "@/components/IconSprite";
import CommandPalette from "@/components/CommandPalette";
import LoginPanel from "@/components/LoginPanel";

const initials = (name?: string) => {
  if (!name) return "AD";
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "AD";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const AdminLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const profile = useAuth((s) => s.profile);
  const fetchProfile = useAuth((s) => s.fetchProfile);
  const logout = useAuth((s) => s.logout);
  const hasFetched = useAuth((s) => s.hasFetched);
  const loading = useAuth((s) => s.loading);

  const { canView, isSuperadmin } = usePermissions();

  const theme = useUi((s) => s.theme);
  const initTheme = useUi((s) => s.initTheme);
  const toggleTheme = useUi((s) => s.toggleTheme);
  const togglePalette = useUi((s) => s.togglePalette);
  const openPalette = useUi((s) => s.openPalette);
  const closePalette = useUi((s) => s.closePalette);
  const collapsed = useUi((s) => s.sidebarCollapsed);
  const toggleSidebar = useUi((s) => s.toggleSidebar);

  const toastMsg = useToast((s) => s.message);
  const toastVisible = useToast((s) => s.visible);

  useEffect(() => {
    initTheme();
  }, [initTheme]);

  useEffect(() => {
    if (!hasFetched) {
      fetchProfile().catch(() => undefined);
    }
  }, [fetchProfile, hasFetched]);

  // Global keyboard: ⌘K palette, ⌘⇧L theme, Esc closes palette.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        togglePalette();
      } else if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key.toLowerCase() === "l") {
        e.preventDefault();
        toggleTheme();
      } else if (e.key === "Escape") {
        closePalette();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [togglePalette, toggleTheme, closePalette]);

  const visibleGroups = useMemo(
    () =>
      navGroups
        .map((group) => ({
          ...group,
          items: group.items.filter((item) => {
            if (item.superadminOnly) return isSuperadmin;
            return !item.resource || isSuperadmin || canView(item.resource);
          }),
        }))
        .filter((group) => group.items.length > 0),
    [canView, isSuperadmin],
  );

  const [crumbGroup, crumbCurrent] = crumbForPath(location.pathname);

  if (!hasFetched && loading) {
    return (
      <div style={{ height: "100vh", display: "grid", placeItems: "center" }}>
        <p className="meta">Loading…</p>
      </div>
    );
  }

  if (!profile) {
    return <LoginPanel />;
  }

  return (
    <>
      <div className={clsx("app", collapsed && "collapsed")}>
        {/* ================= SIDEBAR ================= */}
        <aside className="sb">
          <div className="sb-top">
            <div className="mark">
              <Icon name="leaf" />
            </div>
            <div className="wordmark">
              Santé <em className="sub" style={{ fontStyle: "normal" }}>AI</em>
            </div>
          </div>

          <div className="sb-cta">
            <button className="qs" onClick={openPalette}>
              <Icon name="search" />
              <span>Search…</span>
              <span className="kbd">⌘K</span>
            </button>
          </div>

          <nav className="sb-nav">
            {visibleGroups.map((group) => (
              <div className="grp" key={group.label}>
                <span className="grp-label">{group.label}</span>
                {group.items.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.to === "/"}
                    data-tip={item.label}
                    className={({ isActive }) => clsx("nav-a", isActive && "on")}
                  >
                    <Icon name={item.icon} />
                    <span>{item.label}</span>
                    {item.count && (
                      <em className="count" style={{ fontStyle: "normal" }}>
                        {item.count}
                      </em>
                    )}
                    {item.adv && (
                      <em className="adv-flag" style={{ fontStyle: "normal" }}>
                        Adv
                      </em>
                    )}
                  </NavLink>
                ))}
              </div>
            ))}
          </nav>

          <div className="sb-foot">
            <div className="sb-user">
              <span className="av g">{initials(profile.displayName)}</span>
              <span className="sb-foot-txt">
                <b>{profile.displayName || "Admin"}</b>
                <span className={isSuperadmin ? "sa" : undefined}>
                  {isSuperadmin ? "Superadmin" : profile.roleName || "Member"}
                </span>
              </span>
            </div>
          </div>
        </aside>

        {/* ================= MAIN ================= */}
        <div className="main">
          <header className="tb">
            <button className="icon-btn" onClick={toggleSidebar} title="Collapse sidebar">
              <Icon name="panel" />
            </button>
            <nav className="crumbs">
              <a onClick={() => navigate("/")} style={{ cursor: "pointer" }}>
                Santé AI
              </a>
              {crumbGroup && (
                <>
                  <span className="sep">/</span>
                  <span>{crumbGroup}</span>
                </>
              )}
              <span className="sep">/</span>
              <span className="cur">{crumbCurrent || "Dashboard"}</span>
            </nav>

            <div className="tb-r">
              <span className="env">
                {/* Label comes from the build env so a UAT build can never
                    masquerade as production in the header. */}
                <i className="pulse" /> {import.meta.env.VITE_ENV_LABEL || "Production"}
              </span>
              <span className="tb-div" />
              <button className="icon-btn" onClick={openPalette} title="Command palette (⌘K)">
                <Icon name="search" />
              </button>
              <button
                className="icon-btn"
                onClick={() => toast("No new notifications")}
                title="Notifications"
                style={{ position: "relative" }}
              >
                <Icon name="bell" />
              </button>
              <button className="icon-btn" onClick={toggleTheme} title="Toggle theme (⌘⇧L)">
                <Icon name={theme === "dark" ? "moon" : "sun"} />
              </button>
              <span className="tb-div" />
              <button className="btn sec sm" onClick={() => logout().catch(() => undefined)}>
                <Icon name="out" />
                Sign out
              </button>
            </div>
          </header>

          <div className="scroll" id="scroll">
            <Outlet />
          </div>
        </div>
      </div>

      <CommandPalette />

      <div className={clsx("toast", toastVisible && "show")}>
        <Icon name="check" />
        <span>{toastMsg}</span>
      </div>
    </>
  );
};

export default AdminLayout;
